import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Database } from "@/integrations/supabase/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useForm } from "react-hook-form";
import { AIProviderSettings } from "./AIProviderSettings";
// import { AIChatModeSettings } from "./AIChatModeSettings";
import { AIChatbotSettings as AIChatbotSettingsType } from "@/types/ai";
import { useToast } from "@/components/ui/use-toast";
import { useBuilder } from "@/contexts/BuilderContext";
// import { useAIChatModeConfig } from "@/hooks/useAIChatModeConfig";

type ChatbotData = Database['public']['Tables']['chatbots']['Row'];

interface GeneralChatbotSettings {
  messageDelay: number;
  showTypingIndicator: boolean;
  chatMode?: 'question_flow' | 'ai_only'; // New chat mode setting
  buttonOptionsDelay?: number; // Delay before showing button options (0 = immediate)
  noResponseDelay?: number; // Delay for no_response questions to let users read
  optionsHandlerDelay?: number; // Delay in options handler for event dispatching
  validation_settings?: {
    enabled: boolean;
    maxAttempts: number;
    strictValidation: boolean;
    followUpStyle?: string;
  };
  ai_settings?: AIChatbotSettingsType;
}

interface CustomCodeFormValues {
  customJavaScript: string;
  customHtml: string;
}

interface BuilderSettingsProps {
  chatbot: ChatbotData | null | undefined;
  onUpdateSettings: (updates: Partial<ChatbotData>) => void;
  reportUnsavedChanges: (isDirty: boolean) => void;
}

export const BuilderSettings = ({
  chatbot,
  onUpdateSettings,
  reportUnsavedChanges
}: BuilderSettingsProps) => {
  const { toast } = useToast();

  // Use unified builder context instead of local state
  const { getChatbot, getSettings, updateSettings, debouncedSave, state } = useBuilder();
  const unifiedChatbot = getChatbot();
  const unifiedSettings = getSettings();

  // Local state only for UI-specific values that don't need persistence
  const [active, setActive] = useState(chatbot?.active || false);
  const [isSavingAISettings, setIsSavingAISettings] = useState(false);

  // Get current values from unified settings
  const messageDelay = unifiedSettings?.messageDelay ?? 1000;
  const showTypingIndicator = unifiedSettings?.showTypingIndicator ?? true;
  const buttonOptionsDelay = unifiedSettings?.buttonOptionsDelay ?? 0;
  const noResponseDelay = unifiedSettings?.noResponseDelay ?? 2000;
  const optionsHandlerDelay = unifiedSettings?.optionsHandlerDelay ?? 10;

  // Get validation settings from unified context
  const validationEnabled = unifiedSettings?.validation_settings?.enabled ?? false;
  const validationMaxAttempts = unifiedSettings?.validation_settings?.maxAttempts ?? 2;
  const validationStrict = unifiedSettings?.validation_settings?.strictValidation ?? false;
  
  const [aiSettings, setAISettings] = useState<AIChatbotSettingsType>({
    messageStyle: 'traditional',
    typingSpeed: 400,
    messageDelay: 500,
    showTypingIndicator: true,
    streamingForAIOnly: true,
    aiServices: {
      primary: {
        provider: 'openai',
        model: 'gpt-4o',
        temperature: 0.7,
        max_tokens: 2048
      }
    }
  });
  


  // AI Chat Mode Configuration
  // const aiChatConfig = useAIChatModeConfig(chatbot?.id || '');

  const customCodeForm = useForm<CustomCodeFormValues>({
    defaultValues: {
      customJavaScript: chatbot?.custom_javascript || '',
      customHtml: chatbot?.custom_html || ''
    }
  });

  const handleAISettingsChange = (newSettings: AIChatbotSettingsType) => {
    setAISettings(newSettings);
    reportUnsavedChanges(true);
  };

  const saveAISettings = async () => {
    if (!chatbot) return;
    
    setIsSavingAISettings(true);
    
    try {
      const currentOverallSettings = (chatbot.settings as GeneralChatbotSettings) || { messageDelay: 1000, showTypingIndicator: true };
      const newOverallSettings: GeneralChatbotSettings = {
        ...currentOverallSettings,
        ai_settings: aiSettings 
      };

      onUpdateSettings({ settings: newOverallSettings as any });
      reportUnsavedChanges(true); 

      toast({
        title: "AI Settings Update Initiated",
        description: "Your AI chatbot settings update has been sent."
      });

    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Save Failed",
        description: error.message || "Failed to save AI settings."
      });
    } finally {
      setIsSavingAISettings(false);
    }
  };
  
  // Simple effect to sync active state from props
  useEffect(() => {
    if (chatbot) {
      setActive(chatbot.active || false);

      customCodeForm.reset({
        customJavaScript: chatbot.custom_javascript || '',
        customHtml: chatbot.custom_html || ''
      });
    }
  }, [chatbot, customCodeForm]);

  const handleActiveToggle = (checked: boolean) => {
    setActive(checked);
    onUpdateSettings({ active: checked });
    reportUnsavedChanges(true);
  };

  const handleShowTypingIndicatorToggle = (checked: boolean) => {
    console.log('🎚️ Typing indicator changed to:', checked);

    updateSettings({ showTypingIndicator: checked });
    reportUnsavedChanges(true);
  };



  const handleMessageDelayChange = (value: number[]) => {
    const delay = value[0];
    console.log('🎚️ Message delay slider changed to:', delay, 'Type:', typeof delay);

    // Use unified context to update settings
    updateSettings({ messageDelay: delay });
    reportUnsavedChanges(true);
  };

  const handleButtonOptionsDelayChange = (value: number[]) => {
    const delay = value[0];
    console.log('🎚️ Button options delay changed to:', delay);

    updateSettings({ buttonOptionsDelay: delay });
    reportUnsavedChanges(true);
  };

  const handleNoResponseDelayChange = (value: number[]) => {
    const delay = value[0];
    console.log('🎚️ No response delay changed to:', delay);

    updateSettings({ noResponseDelay: delay });
    reportUnsavedChanges(true);
  };

  const handleOptionsHandlerDelayChange = (value: number[]) => {
    const delay = value[0];
    console.log('🎚️ Options handler delay changed to:', delay);

    updateSettings({ optionsHandlerDelay: delay });
    reportUnsavedChanges(true);
  };

  const handleValidationEnabledChange = (enabled: boolean) => {
    console.log('🎚️ Validation enabled changed to:', enabled);

    updateSettings({
      validation_settings: {
        ...unifiedSettings?.validation_settings,
        enabled
      }
    });
    reportUnsavedChanges(true);
  };

  const handleValidationMaxAttemptsChange = (maxAttempts: number) => {
    console.log('🎚️ Validation max attempts changed to:', maxAttempts);

    updateSettings({
      validation_settings: {
        ...unifiedSettings?.validation_settings,
        maxAttempts
      }
    });
    reportUnsavedChanges(true);
  };

  const handleValidationStrictChange = (strictValidation: boolean) => {
    console.log('🎚️ Validation strict changed to:', strictValidation);

    updateSettings({
      validation_settings: {
        ...unifiedSettings?.validation_settings,
        strictValidation
      }
    });
    reportUnsavedChanges(true);
  };

  const handleTcpaLanguageChange = (tcpaLanguage: string) => {
    console.log('📞 TCPA language changed to:', tcpaLanguage);

    updateSettings({
      tcpaLanguage
    });
    reportUnsavedChanges(true);
  };

  const onSubmitCustomCode = (data: CustomCodeFormValues) => {
    onUpdateSettings({ 
      custom_javascript: data.customJavaScript,
      custom_html: data.customHtml
    });
    reportUnsavedChanges(true);
    toast({ title: "Custom Code Update Initiated" });
  };

  if (!chatbot) {
    return <div>Loading settings...</div>; 
  }

  return (
    <Tabs defaultValue="general" className="space-y-4">
      <TabsList>
        <TabsTrigger value="general">General</TabsTrigger>
        <TabsTrigger value="aiProvider">AI Provider</TabsTrigger>
        <TabsTrigger value="customCode">Custom Code</TabsTrigger>
      </TabsList>

      <TabsContent value="general" className="space-y-6">
        <div className="p-6 border rounded-lg shadow-sm bg-card">
          <h3 className="text-lg font-medium mb-4">General Settings</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="active-toggle">Chatbot Active</Label>
              <Switch 
                id="active-toggle" 
                checked={active} 
                onCheckedChange={handleActiveToggle} 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message-delay">Message Delay (ms)</Label>
              <Slider 
                id="message-delay"
                min={0} 
                max={5000} 
                step={100} 
                value={[messageDelay]} 
                onValueChange={handleMessageDelayChange} 
              />
              <p className="text-sm text-muted-foreground">Current: {messageDelay}ms</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="button-options-delay">Button Options Delay (ms)</Label>
              <Slider 
                id="button-options-delay"
                min={0} 
                max={5000} 
                step={100} 
                value={[buttonOptionsDelay]} 
                onValueChange={handleButtonOptionsDelayChange} 
              />
              <p className="text-sm text-muted-foreground">Current: {buttonOptionsDelay}ms</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="no-response-delay">No Response Delay (ms)</Label>
              <Slider 
                id="no-response-delay"
                min={0} 
                max={5000} 
                step={100} 
                value={[noResponseDelay]} 
                onValueChange={handleNoResponseDelayChange} 
              />
              <p className="text-sm text-muted-foreground">Current: {noResponseDelay}ms</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="options-handler-delay">Options Handler Delay (ms)</Label>
              <Slider 
                id="options-handler-delay"
                min={0} 
                max={5000} 
                step={100} 
                value={[optionsHandlerDelay]} 
                onValueChange={handleOptionsHandlerDelayChange} 
              />
              <p className="text-sm text-muted-foreground">Current: {optionsHandlerDelay}ms</p>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="typing-indicator">Show Typing Indicator</Label>
              <Switch
                id="typing-indicator"
                checked={showTypingIndicator}
                onCheckedChange={handleShowTypingIndicatorToggle}
              />
            </div>

          </div>
        </div>

        <div className="p-6 border rounded-lg shadow-sm bg-card">
          <h3 className="text-lg font-medium mb-4">Validation Settings</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="validation-enabled">Enable Validation</Label>
              <Switch
                id="validation-enabled"
                checked={validationEnabled}
                onCheckedChange={handleValidationEnabledChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="validation-max-attempts">Max Attempts</Label>
              <Input
                id="validation-max-attempts"
                type="number"
                value={validationMaxAttempts}
                onChange={(e) => handleValidationMaxAttemptsChange(parseInt(e.target.value))}
                disabled={!validationEnabled}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="validation-strict">Strict Validation</Label>
              <Switch
                id="validation-strict"
                checked={validationStrict}
                onCheckedChange={handleValidationStrictChange}
                disabled={!validationEnabled}
              />
            </div>
          </div>
        </div>

        <div className="p-6 border rounded-lg shadow-sm bg-card">
          <h3 className="text-lg font-medium mb-4">TCPA Compliance</h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tcpa-language">TCPA Warning Message</Label>
              <Textarea
                id="tcpa-language"
                placeholder="Enter the TCPA compliance message that will be shown when users are asked for their phone number..."
                value={unifiedSettings?.tcpaLanguage || ''}
                onChange={(e) => handleTcpaLanguageChange(e.target.value)}
                rows={3}
                className="resize-vertical"
              />
              <p className="text-sm text-muted-foreground">
                This message automatically appears when the chatbot asks for a phone number to ensure TCPA compliance.
              </p>
            </div>
          </div>
        </div>
      </TabsContent>

      <TabsContent value="aiProvider">
        <AIProviderSettings 
          settings={aiSettings} 
          onSettingsChange={handleAISettingsChange} 
        />
        <Button onClick={saveAISettings} disabled={isSavingAISettings} className="mt-4">
          {isSavingAISettings ? "Saving..." : "Save AI Settings"}
        </Button>
      </TabsContent>


      <TabsContent value="customCode">
        <Form {...customCodeForm}>
          <form onSubmit={customCodeForm.handleSubmit(onSubmitCustomCode)} className="space-y-8 p-6 border rounded-lg shadow-sm bg-card">
            <h3 className="text-lg font-medium mb-4">Custom Code Injection</h3>
            <FormField
              control={customCodeForm.control}
              name="customJavaScript"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Custom JavaScript</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Enter custom JavaScript here..." {...field} onChange={(e) => { field.onChange(e); reportUnsavedChanges(true); }} />
                  </FormControl>
                  <FormDescription>
                    JavaScript code to be injected into the chat widget.
                  </FormDescription>
                </FormItem>
              )}
            />
            <FormField
              control={customCodeForm.control}
              name="customHtml"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Custom HTML</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Enter custom HTML here..." {...field} onChange={(e) => { field.onChange(e); reportUnsavedChanges(true); }} />
                  </FormControl>
                  <FormDescription>
                    HTML content to be injected into the chat widget.
                  </FormDescription>
                </FormItem>
              )}
            />
            <Button type="submit">Save Custom Code</Button>
          </form>
        </Form>
      </TabsContent>
    </Tabs>
  );
};
